/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

import { ReactNode } from 'react'

// @mui material components
import Card from '@mui/material/Card'
import Divider from '@mui/material/Divider'
import Icon from '@mui/material/Icon'
import Tooltip from '@mui/material/Tooltip'

// Material Dashboard 2 PRO React TS components
import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDTypography from 'components/MDTypography'
import LoadingComponent from 'modules/Loading'

// Declaring props types for CompleStatisticsCard
interface Props {
	color?: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error' | 'light' | 'dark'
	title: string
	count: string | number
	percentage?: {
		color: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error' | 'dark' | 'white'
		amount: string | number
		label: string
	}
	icon: ReactNode
	tooltip?: string
	[key: string]: any
}

function ComplexStatisticsCard({
	color,
	title,
	count,
	percentage,
	icon,
	tooltip,
}: Props): JSX.Element {
	return (
		<Card>
			<MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
				<MDBox
					variant="gradient"
					bgColor={color}
					color={color === 'light' ? 'dark' : 'white'}
					coloredShadow={color}
					borderRadius="xl"
					display="flex"
					justifyContent="center"
					alignItems="center"
					width="4rem"
					height="4rem"
					mt={-3}
				>
					<Icon fontSize="medium" color="inherit">
						{icon}
					</Icon>
				</MDBox>
				<MDBox textAlign="right" lineHeight={1.25}>
					<MDBox display="flex" alignItems="center" justifyContent="flex-end" gap={2}>
						{tooltip && (
							<Tooltip title={tooltip} placement="left" arrow>
								<MDButton variant="outlined" color="secondary" size="small" circular iconOnly>
									<Icon>priority_high</Icon>
								</MDButton>
							</Tooltip>
						)}
						<MDTypography variant="button" fontWeight="light" color="text">
							{title}
						</MDTypography>
					</MDBox>
					<MDTypography variant="h4">{count}</MDTypography>
				</MDBox>
			</MDBox>
			<Divider />
			<MDBox pb={2} px={2}>
				<MDTypography
					component="p"
					variant="button"
					color="text"
					display="flex"
					alignItems="center"
				>
					{percentage.amount ? (
						<MDTypography
							component="span"
							variant="button"
							fontWeight="bold"
							color={percentage.color}
						>
							{percentage.amount}
						</MDTypography>
					) : (
						<LoadingComponent type="box" height={32} />
					)}
					&nbsp;{percentage.label}
				</MDTypography>
			</MDBox>
		</Card>
	)
}

// Declaring defualt props for ComplexStatisticsCard
ComplexStatisticsCard.defaultProps = {
	color: 'info',
	percentage: {
		color: 'success',
		text: '',
		label: '',
	},
}

export default ComplexStatisticsCard
